import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Box } from "@mui/material";
import { AppQuill } from "../../../../Component/Common/Components/FormValidationInputs/AppQuill";
import { WorkforceProfileGoalsAndTargetsService } from "../../../../Component/Api/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileGoalsAndTargetsService";
import { WorkforceProfileGoalsAndTargets } from "../../../../Component/Model/Dto/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileGoalsAndTargets";
import { EEOccupationalLevel } from "../../../../Component/Model/Enum/EmploymentEquity";
import { workforceCommentSchema } from "./WorkforceCommentValidation";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import { useRecoilValue } from "recoil";
import { EEScorecardAtom } from "../../../../State/EEScorecard.Atom";
import { ConsolidationAtom } from "../../../../State/Consolidation.Atom";

interface IProps {
    comments?: WorkforceProfileGoalsAndTargets.CommentsDtoOut[];
    occupationalLevel: EEOccupationalLevel;
    scorecardId: string;
    isDisabled: boolean;
}

type CommentFormData = {
    comment: string;
}

export const WorkforceCommentsNew: FC<IProps> = (props) => {
    const [initialComment, setInitialComment] = useState<WorkforceProfileGoalsAndTargets.CommentsDtoOut | null>(null);
    const initialCommentRef = useRef<WorkforceProfileGoalsAndTargets.CommentsDtoOut | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const scorecard = useRecoilValue(EEScorecardAtom);
    const isConsolidatedScorecard = useRecoilValue(ConsolidationAtom);
    const maxConsolidatedCommentLength = 3000 * (scorecard.ConsolidationSourceScorecardsCount ?? 1);
    const maxCommentLength = isConsolidatedScorecard ? maxConsolidatedCommentLength : 3000;

    const { control, reset, getValues } = useForm<CommentFormData>({
        mode: 'all',
        resolver: yupResolver(workforceCommentSchema(maxCommentLength))
    });

    useEffect(() => {
        const matchingComment = props.comments?.find(comment => comment.occupationalLevel === props.occupationalLevel);
        if (matchingComment) {
            setInitialComment(matchingComment);
            initialCommentRef.current = matchingComment;
            reset({ comment: matchingComment.comment });
        } else {
            setInitialComment(null);
            initialCommentRef.current = null;
            reset({ comment: '' });
        }
    }, [props.comments, props.occupationalLevel, reset]);


    const saveCommentForm = async () => {
        const formData = getValues();

        if (formData.comment.length > 0) {
            setIsLoading(true);

            workforceCommentSchema(maxCommentLength).validate(formData, { abortEarly: false })
                .then(async () => {
                    const requestData = {
                        commentId: initialCommentRef.current?.commentId,
                        comment: formData.comment,
                        isDisabled: props.isDisabled,
                        occupationalLevel: props.occupationalLevel,
                        scorecardId: props.scorecardId,
                    };

                    const commentId = (await WorkforceProfileGoalsAndTargetsService.SaveComment(requestData)).data;

                    const comment : WorkforceProfileGoalsAndTargets.CommentsDtoOut  = {
                        commentId: commentId,
                        comment: formData.comment,
                        occupationalLevel: props.occupationalLevel,
                    };

                    setInitialComment(comment);
                    initialCommentRef.current = comment;
                    toast.success(`Comment saved successfully`);
                })
                .catch((error: any) => {
                    toast.error(error.message);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    return (
        <Box mt={2}>
            <AppQuill control={control} name='comment' label='Comment' maxLength={maxCommentLength} />
            <Box mt={1}>
                <LoadingButton variant='contained' color='primary' onClick={saveCommentForm} loading={isLoading}>Save</LoadingButton>
            </Box>
            
        </Box>
    );
};
